import { ApiClient, ApiError, type RunpodCustomModel } from '../lib/api.js';
import {
  el,
  formatBytes,
  formatDateTime,
  mount,
} from '../lib/dom.js';

export interface RunpodCustomModelsViewDeps {
  api: ApiClient;
}

/**
 * Standard ComfyUI subfolders. The dropdown ships with the common ones but
 * stays editable so anything custom (e.g. "RMBG/BEN2") can be typed in.
 */
const FOLDER_PRESETS = [
  'checkpoints',
  'loras',
  'vae',
  'text_encoders',
  'diffusion_models',
  'controlnet',
  'clip',
  'clip_vision',
  'unet',
  'upscale_models',
  'embeddings',
  'ipadapter',
] as const;

export function renderRunpodCustomModels(deps: RunpodCustomModelsViewDeps): HTMLElement {
  const card = el('section', { class: 'card' });
  const list = el('div', { class: 'logs-list' });
  const summary = el('span', { class: 'who' });
  const refreshBtn = el('button', { class: 'ghost', text: 'Refresh' });
  const alertBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });

  const showAlert = (msg: string, kind: 'error' | 'success' = 'error') => {
    alertBox.className = kind === 'success' ? 'alert success' : 'alert';
    alertBox.textContent = msg;
    alertBox.removeAttribute('hidden');
    setTimeout(() => alertBox.setAttribute('hidden', ''), 6000);
  };

  // ── Upload form ─────────────────────────────────────────
  const fileInput = el('input', { attrs: { type: 'file' } });
  const nameInput = el('input', {
    attrs: {
      type: 'text',
      placeholder: 'Name (e.g. epicrealism-v6)',
      spellcheck: false,
      autocapitalize: 'off',
      autocorrect: 'off',
    },
  });
  const folderSelect = el('input', {
    attrs: {
      type: 'text',
      list: 'custom-models-folders-presets',
      placeholder: 'Folder (e.g. loras)',
      spellcheck: false,
      autocapitalize: 'off',
      autocorrect: 'off',
    },
  });
  const folderDataList = el('datalist', { attrs: { id: 'custom-models-folders-presets' } });
  for (const f of FOLDER_PRESETS) {
    folderDataList.append(el('option', { attrs: { value: f } }));
  }
  const descInput = el('input', {
    attrs: {
      type: 'text',
      placeholder: 'Description (optional)',
      maxlength: 500,
    },
  });
  const uploadBtn = el('button', {
    class: 'primary',
    text: 'Upload model',
    attrs: { type: 'button' },
  });

  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    if (!nameInput.value.trim()) {
      // Pre-fill with the file's basename, sans extension.
      const base = f.name.replace(/\.[^.]+$/, '').replace(/[^A-Za-z0-9._-]+/g, '-');
      nameInput.value = base;
    }
    if (!folderSelect.value.trim()) {
      // Best-effort folder guess from extension/name.
      const lower = f.name.toLowerCase();
      if (lower.includes('lora')) folderSelect.value = 'loras';
      else if (lower.includes('vae')) folderSelect.value = 'vae';
      else if (lower.includes('controlnet')) folderSelect.value = 'controlnet';
      else if (lower.endsWith('.safetensors') || lower.endsWith('.ckpt')) folderSelect.value = 'checkpoints';
    }
  });

  uploadBtn.addEventListener('click', async () => {
    const file = fileInput.files?.[0];
    if (!file) {
      showAlert('Pick a file first');
      return;
    }
    const name = nameInput.value.trim();
    if (!name) {
      showAlert('Set a name for this model');
      nameInput.focus();
      return;
    }
    const folder = folderSelect.value.trim();
    if (!folder) {
      showAlert('Pick a folder (e.g. loras, checkpoints)');
      folderSelect.focus();
      return;
    }
    uploadBtn.setAttribute('disabled', '');
    const original = uploadBtn.textContent ?? 'Upload model';
    uploadBtn.textContent = `Uploading ${formatBytes(file.size)}…`;
    try {
      const desc = descInput.value.trim();
      await deps.api.uploadRunpodCustomModel({
        name,
        folder,
        file,
        filename: file.name,
        ...(desc ? { description: desc } : {}),
        onProgress: (loaded, total) => {
          if (total > 0) {
            const pct = Math.round((loaded / total) * 100);
            uploadBtn.textContent = `Uploading ${pct}% (${formatBytes(loaded)}/${formatBytes(total)})`;
          }
        },
      });
      showAlert(`Uploaded "${name}"`, 'success');
      fileInput.value = '';
      nameInput.value = '';
      folderSelect.value = '';
      descInput.value = '';
      await refresh();
    } catch (err) {
      showAlert(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      uploadBtn.removeAttribute('disabled');
      uploadBtn.textContent = original;
    }
  });

  const uploadCard = el('section', {
    class: 'card',
    children: [
      el('header', {
        children: [
          el('h2', { text: 'Upload custom model' }),
          el('span', {
            class: 'who',
            text: 'Private LoRAs, checkpoints, VAEs… baked into the image at build time',
          }),
        ],
      }),
      el('div', {
        class: 'form-row',
        children: [
          el('label', {
            class: 'field',
            children: [el('span', { text: 'File' }), fileInput],
          }),
          el('label', {
            class: 'field',
            children: [el('span', { text: 'Name' }), nameInput],
          }),
          el('label', {
            class: 'field',
            children: [el('span', { text: 'Folder' }), folderSelect, folderDataList],
          }),
          el('label', {
            class: 'field',
            children: [el('span', { text: 'Description' }), descInput],
          }),
          uploadBtn,
        ],
      }),
      el('p', {
        class: 'hint',
        text:
          'Files are stored privately on the deploy-app and copied into the Docker '
          + 'image at build time (never published to GitHub or Docker Hub). Pick a '
          + 'name in the editor via local://<name>.',
      }),
    ],
  });

  // ── Existing models list ────────────────────────────────
  const renderRow = (m: RunpodCustomModel): HTMLElement => {
    const head = el('div', {
      class: 'log-row-head',
      children: [
        el('span', { class: 'branch-tag', text: m.name }),
        el('span', {
          class: 'who',
          text: `${formatBytes(m.sizeBytes)} · ${m.attachedBranches.length} endpoint(s)`,
        }),
      ],
    });
    const meta = el('div', {
      class: 'when',
      text: [
        m.originalFilename,
        formatDateTime(m.uploadedAt),
        m.uploadedByUsername ? `by ${m.uploadedByUsername}` : null,
      ].filter((x): x is string => Boolean(x)).join(' · '),
    });

    // Editable folder + description, saved on blur. Uses a borderless input
    // so the row stays visually compact unless the user clicks in.
    const folderInline = el('input', {
      class: 'mono',
      attrs: {
        type: 'text',
        value: m.folder,
        list: 'custom-models-folders-presets',
        spellcheck: false,
        'aria-label': 'Folder',
        title: 'Default ComfyUI subfolder for this model — editable',
      },
    });
    const folderRow = el('div', {
      class: 'who',
      children: ['Folder: ', folderInline],
    });
    folderInline.addEventListener('blur', async () => {
      const next = folderInline.value.trim();
      if (next === m.folder || next.length === 0) {
        folderInline.value = m.folder;
        return;
      }
      try {
        const res = await deps.api.updateRunpodCustomModel(m.id, { folder: next });
        m.folder = res.item.folder;
        folderInline.value = res.item.folder;
        showAlert(`Folder updated for "${m.name}"`, 'success');
      } catch (err) {
        folderInline.value = m.folder;
        showAlert(err instanceof ApiError ? err.message : 'Folder update failed');
      }
    });

    const descInline = el('input', {
      attrs: {
        type: 'text',
        value: m.description ?? '',
        placeholder: 'Add a description…',
        maxlength: 500,
        'aria-label': 'Description',
      },
    });
    const descRow = el('div', {
      class: 'who',
      children: ['Description: ', descInline],
    });
    descInline.addEventListener('blur', async () => {
      const next = descInline.value.trim();
      if (next === (m.description ?? '')) return;
      try {
        const res = await deps.api.updateRunpodCustomModel(m.id, { description: next });
        m.description = res.item.description;
        descInline.value = res.item.description ?? '';
        showAlert(`Description updated for "${m.name}"`, 'success');
      } catch (err) {
        descInline.value = m.description ?? '';
        showAlert(err instanceof ApiError ? err.message : 'Description update failed');
      }
    });

    const branches = m.attachedBranches.length > 0
      ? el('div', {
          class: 'who',
          text: `Used by: ${m.attachedBranches.join(', ')}`,
        })
      : el('div', {
          class: 'who',
          text: 'Not attached to any endpoint',
        });
    const sha = el('div', {
      class: 'who mono',
      text: `local://${m.name} · sha256: ${m.sha256.slice(0, 16)}…`,
    });
    const metaCol = el('div', {
      class: 'log-meta',
      children: [head, meta, folderRow, descRow, branches, sha],
    });

    const downloadBtn = el('a', {
      class: 'btn',
      text: 'Download',
      attrs: {
        href: deps.api.runpodCustomModelDownloadUrl(m.id),
        download: m.originalFilename,
      },
    });
    const deleteBtn = el('button', {
      class: 'danger',
      text: 'Delete',
      on: {
        click: async () => {
          if (m.attachedBranches.length > 0) {
            showAlert(
              `Detach "${m.name}" from ${m.attachedBranches.length} endpoint(s) first`,
            );
            return;
          }
          if (!confirm(`Delete custom model "${m.name}"? This cannot be undone.`)) return;
          try {
            await deps.api.deleteRunpodCustomModel(m.id);
            showAlert(`Deleted "${m.name}"`, 'success');
            await refresh();
          } catch (err) {
            showAlert(err instanceof ApiError ? err.message : 'Delete failed');
          }
        },
      },
    });

    return el('div', {
      class: 'log-row',
      children: [metaCol, el('div', { class: 'log-actions', children: [downloadBtn, deleteBtn] })],
    });
  };

  const refresh = async () => {
    refreshBtn.setAttribute('disabled', '');
    try {
      const { items } = await deps.api.listRunpodCustomModels();
      const total = items.reduce((acc, m) => acc + m.sizeBytes, 0);
      summary.textContent = items.length === 0
        ? 'No custom models yet'
        : `${items.length} model(s) · ${formatBytes(total)} total`;
      if (items.length === 0) {
        mount(list, [el('div', { class: 'empty', text: 'No custom models uploaded yet.' })]);
      } else {
        mount(list, items.map(renderRow));
      }
    } catch (err) {
      showAlert(err instanceof ApiError ? err.message : 'Failed to load custom models');
    } finally {
      refreshBtn.removeAttribute('disabled');
    }
  };
  refreshBtn.addEventListener('click', () => void refresh());

  card.append(
    el('header', {
      children: [
        el('div', { class: 'modal-title', children: [el('h2', { text: 'Custom models' }), summary] }),
        el('div', { class: 'log-actions', children: [refreshBtn] }),
      ],
    }),
    alertBox,
    list,
  );

  void refresh();

  return el('div', { children: [uploadCard, card] });
}
